import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  admin,
  endPoints,
  leadStatusObj,
  leadStatusOptions,
  noProject,
  noTeam,
  noUser,
  propertyStatusObj,
  propertyStatusOptions,
  sharedLeads,
  taskStatusObj,
  taskStatusOptions,
  teams,
  userRoleObj,
} from "./constants";
import { NotificationManager } from "react-notifications";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { crudService } from "services/crud.service";
import parsePhoneNumberFromString from "libphonenumber-js";

const {
  leadEndPoint,
  tokenEndPoint,
  inventoryEndPoint,
  projectBlockEndPoint,
  individualPropertyEndPoint,
  projectPhaseEndPoint,
  projectAssignmentEndPoint,
  teamProjectAssignmentEndPoint,
  userProjectAssignmentEndPoint,
  contactEndPoint,
} = endPoints;
const { propertyAvailable } = propertyStatusObj;

export function hexToRgb(hex: string): string {
  let r = 0,
    g = 0,
    b = 0;
  // 3 digits
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  }
  // 6 digits
  else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  return `${r},${g},${b}`;
}

export function isNumeric(value: any) {
  return /^[0-9+.]+$/.test(value); // Allow only digits, "." and "+"
}

export function isNumericWithDashes(value: any) {
  return /^[0-9-]+$/.test(value); // Allows digits, "+" and "-"
}

export function isValidPropertyNumber(value: any) {
  // ^ start with digit
  // optional letter
  // ? digits optional

  return /^[0-9]{1,7}$|^[0-9]+[-/][A-Za-z]?[0-9]*$/.test(value);
}

export function isValidPakistaniPhoneNumber(number: any) {
  // Regular expression for validating Pakistani phone numbers
  const regex = /^(?:\+92|92|0)?(3[0-9]{2})[0-9]{7}$/;
  return regex.test(number);
}

export function isValidEmail(email: string) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
  return regex.test(email);
}

export function getLeadOptions(leadsData: any) {
  const { objects = [], meta } = leadsData || {};

  return objects.map((item: any) => {
    return {
      ...item,
      label: item?.phone_number,
      value: item?.phone_number,
    };
  });
}

export function getProjectOptions(projectsData: any) {
  const { objects = [], meta } = projectsData || {};
  return objects.map((item: any, idx: number) => {
    return {
      ...item,
      label: item?.project_name,
      value: item?.project_name + "-" + idx,
    };
  });
}

export function getCitiesOptions(citiesData: any) {
  const objects = citiesData || [];
  return objects.map((item: any, idx: number) => {
    return {
      ...item,
      label: item?.name,
      value: item?.name + "-" + idx,
    };
  });
}
type GetAssignedUserOptionsTypes = {
  data: any;
  isAdmin?: boolean;
  userDetail?: { [key: string]: any };
  variant?: string;
};

export function getAssignedUserOptions({
  data,
  // isAdmin,
  userDetail,
  variant,
}: GetAssignedUserOptionsTypes) {
  const { objects = [], meta } = data || {};

  if (variant === sharedLeads) {
    return objects
      .filter((item: any) => item?.username !== userDetail?.username)
      .map((state: any) => {
        return {
          ...state,
          label: state.username,
          value: state.username,
        };
      });
  }

  if (variant === teams) {
    return objects
      .filter((item: any) => item?.role === userRoleObj.manager)
      .map((state: any) => {
        return {
          ...state,
          label: state.username,
          value: state.username,
        };
      });
  }

  return objects.map((item: any) => {
    return {
      ...item,
      label: item?.username,
      value: item?.username,
    };
  });
}

export function getSourceOptions(sourceData: any) {
  const { objects = [], meta } = sourceData || {};
  return objects.map((item: any) => {
    return {
      ...item,
      label: item?.source,
      value: item?.source,
    };
  });
}

export function getPropertyUnitOptions(propertyData: any, value?: string) {
  const properties = propertyData.map((item: any) => {
    return {
      ...item,
      label: item?.property_no,
      value: item?.property_no,
    };
  });
  return !!value
    ? properties.filter((item: any) => {
        return (
          item?.property_status === propertyAvailable ||
          item?.property_no === value
        );
      })
    : properties.filter(
        (item: any) => item?.property_status === propertyAvailable
      );
}

export function tasksDataManipulationForTable({
  tasksData = [],
}: {
  tasksData: any;
}) {
  return tasksData.map((item: any) => ({
    id: item?.task_id || "-",
    task: item?.task_description || "-",
    lead: item?.lead?.phone_number || "-",
    leadId: item?.lead?.lead_id || "-",
    project_name: item?.lead?.project?.project_name || "-",
    customer: item?.lead?.customer?.name || "-",
    customerId: item?.lead?.customer?.customer_id || "-",
    assigned_user: item?.assigned_user?.username || "-",
    lead_assigned_user: item?.lead?.assigned_user?.username || "-",
    lead_shared_user: item?.lead?.shared_user?.username || "-",
    createdby_user: item?.createdby_user?.username || "-",
    user_phone_number: item?.assigned_user?.phone_number || "",
    task_status: item?.task_status || "-",
    task_priority: item?.task_priority || "-",
    date_created: item?.date_created || "-",
    due_date: item?.due_date || "-",
    date_closed: item?.date_closed || "-",
  }));
  // .sort((a: any, b: any) => a.assigned_user.localeCompare(b.assigned_user));
}

export function commentsDataManipulationForTable({
  commentsData = [],
}: {
  commentsData: any;
}) {
  return commentsData.map((item: any) => ({
    timestamp: item?.created_date,
    agent: item?.user?.username,
    comment: item?.comment,
  }));
}

export function callRecordDataManipulationForTable({
  callRecordData = [],
}: any) {
  return callRecordData.map((item: any) => {
    return {
      callRecordId: item?.call_id || "-",
      leadId: item?.lead?.lead_id || "-",
      customerId: item?.lead?.customer?.customer_id || "-",
      timestamp: item?.timestamp || "-",
      agent: item?.user?.username || "-",
      lead: item?.lead_mobile_number || "-",
      lead_assigned_user: item?.lead?.assigned_user?.username || "-",
      lead_shared_user: item?.lead?.shared_user?.username || "-",
      project: item?.lead?.project?.project_name || "-",
      callType: item?.call_direction || "-",
      customer: item?.lead?.customer?.name || "-",
      recording: item?.recording,
    };
  });
}

export function removeEmptyKeys(dataObj: any) {
  return Object.keys(dataObj).reduce((acc, key) => {
    if (
      dataObj[key] !== null &&
      dataObj[key] !== undefined &&
      dataObj[key] !== ""
    ) {
      acc[key] = dataObj[key];
    }
    return acc;
  }, {});
}

export function capitalizeFirstLetter(str: string) {
  if (!str) return "";

  if (str.includes("-")) {
    const words = str.split("-");
    return words
      .map((word: string) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  }

  if (str.includes(" ")) {
    const words = str.split(" ");
    return words
      .map((word: string) => {
        return word.charAt(0).toUpperCase() + word.slice(1)?.toLowerCase();
      })
      .join(" ");
  }

  if (str.includes("/")) {
    const words = str.split("/");
    return words
      .map((word: string) => {
        return word.charAt(0).toUpperCase() + word.slice(1)?.toLowerCase();
      })
      .join("/");
  }

  return str.charAt(0).toUpperCase() + str.slice(1)?.toLowerCase();
}

export function parseData(data: any) {
  if (!data) return null;
  return JSON.parse(data);
}

export function stringifyData(data: any) {
  if (!data) return null;
  return JSON.stringify(data);
}

export const exportToExcel = (tableData: any) => {
  const worksheet = XLSX.utils.json_to_sheet(tableData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  // Convert to Excel file and trigger download
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

  const data = new Blob([excelBuffer], { type: "application/octet-stream" });

  const now = new Date();
  const formattedDate = now.toISOString().split("T")[0]; // "2025-05-20"
  const formattedTime = now.toTimeString().split(" ")[0].replace(/:/g, "-"); // "14-36-05"
  const filename = `Leads_Export_${formattedDate}_${formattedTime}.xlsx`;

  saveAs(data, filename);
};

export const importedFileToJSON = (file: any): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    reader.onload = (e: any) => {
      try {
        let jsonData: any[];
        if (fileExtension === "csv") {
          const fileData = e.target.result;
          const workbook = XLSX.read(fileData, { type: "string" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          jsonData = XLSX.utils.sheet_to_json(worksheet);
        } else {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          // Get the first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Convert to JSON
          jsonData = XLSX.utils.sheet_to_json(worksheet);
        }

        resolve(jsonData);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};

export async function checkLeadExistence({ phoneNumber }) {
  try {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: 1,
      endpoint: leadEndPoint,
      searchParams: {
        phone_number: phoneNumber,
      },
    });

    return await crudService.getAll(paginatedUrl);
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

export async function checkTokenExistence({ id }) {
  try {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: 1,
      endpoint: tokenEndPoint,
      searchParams: {
        lead__lead_id: id,
      },
    });

    return await crudService.getAll(paginatedUrl);
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

export async function checkContactExistence({ phone_number }) {
  try {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: 1,
      endpoint: contactEndPoint,
      searchParams: {
        phone_number,
      },
    });

    return await crudService.getAll(paginatedUrl);
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

export async function getContactsByCustomerId({ customerId }) {
  try {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: 1,
      limit: 1,
      endpoint: contactEndPoint,
      searchParams: {
        customer__customer_id: customerId,
      },
    });

    return await crudService.getAll(paginatedUrl);
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

export async function fetchMatchingLeads({ phoneNumber }) {
  try {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: 1,
      endpoint: leadEndPoint,
      limit: 5,
      searchParams: {
        phone_number__icontains: phoneNumber,
      },
    });

    return await crudService.getAll(paginatedUrl);
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

export async function getLatestPropertyData({ id }) {
  try {
    const result = await crudService.getById(inventoryEndPoint, id);
    return result.data;
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

export async function getLatestIndividualPropertyData({ id }) {
  try {
    const result = await crudService.getById(individualPropertyEndPoint, id);
    return result.data;
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

export async function getTeamProjects({ name }) {
  try {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: 1,
      endpoint: teamProjectAssignmentEndPoint,
      searchParams: {
        name: name,
      },
    });
    const result = await crudService.getAll(paginatedUrl);
    return result.data;
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

// lead cache outside so that it can persist state
const leadCache: Record<string, any> = {};
const leadPromiseCache: Record<string, Promise<any>> = {};

export async function getLeadByLeadId({ lead_id }: any) {
  // Check if it exists in memory
  if (leadCache[lead_id]) {
    return leadCache[lead_id];
  }

  // Return same stored promise to prevent same api call
  if (leadPromiseCache[lead_id]) {
    return await leadPromiseCache[lead_id];
  }

  // Promise is created for each unique id;

  const requestPromise = (async () => {
    try {
      const result = await crudService.getById(leadEndPoint, lead_id);

      // Store in memory
      Object.assign(leadCache, { [lead_id]: result.data });

      return result.data;
    } catch (error) {
      const message = getErrorMessage(error);
      NotificationManager.error(message);

      return error?.response;
    } finally {
      delete leadPromiseCache[lead_id];
    }
  })();

  // Promise is stored against lead id

  leadPromiseCache[lead_id] = requestPromise;

  return await requestPromise;
}

export async function getUserProjects({ id }) {
  try {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: 1,
      endpoint: userProjectAssignmentEndPoint,
      searchParams: {
        user__id: id,
      },
    });
    const result = await crudService.getAll(paginatedUrl);
    return result.data;
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

export async function getProjectsUserAssignee({ id }) {
  try {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: 1,
      endpoint: userProjectAssignmentEndPoint,
      searchParams: {
        projects__project_id: id,
      },
    });
    const result = await crudService.getAll(paginatedUrl);
    return result.data;
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

export async function getProjectsTeamAssignee({ id }) {
  try {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: 1,
      endpoint: teamProjectAssignmentEndPoint,
      searchParams: {
        assigned_projects__project_id: id,
      },
    });
    const result = await crudService.getAll(paginatedUrl);
    return result.data;
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

export function getUniqueId(): string {
  // return Math.floor(Math.random() * 46655) + 1;
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 8);
  // return crypto.randomUUID();
}

export function emailErrorMessage({
  condition,
  value,
}: {
  condition: boolean;
  value: any;
}): string {
  if (condition) {
    return "";
  }

  if (!value?.length) {
    return "Email shouldn't be empty";
  }

  return "This email format is invalid";
}

export const calculateRange = (meta: { [key: string]: any }) => {
  const { limit, offset, total_count } = meta;

  // Calculate the start and end of the range
  const end = Math.min(offset + limit, total_count);
  const start = end === 0 ? 0 : offset + 1;

  // Return the formatted string
  return `${start}-${end} of ${total_count}`;
};

export function isFilterEmpty(filters: Record<string, any>): boolean {
  return Object.values(filters).every((value) => {
    if (Array.isArray(value)) {
      return value.every((item) => item === null || item === "");
    }

    return value === null || value === "" || value === "+92";
  });
}

export function isPastDate(date: any) {
  const todayDate = moment();
  return moment(date).isBefore(todayDate, "day");
}

export function isValidCNICFormat(cnic: string) {
  // const regex = /^[0-9]{5}-[0-9]{7}-[0-9]{1}$/;
  const digitsOnly = cnic.replace(/\D/g, "");
  return digitsOnly?.length === 13;
}

export function formatCNIC(value: string) {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, "").slice(0, 13); // Limit to 13 digits

  // Format with dashes: 5-7-1
  let formatted = digitsOnly;
  if (digitsOnly.length > 5 && digitsOnly.length <= 12)
    formatted = `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5)}`;
  else if (digitsOnly.length > 12)
    formatted = `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(
      5,
      12
    )}-${digitsOnly.slice(12)}`;

  return formatted;
}

// phase cache to persist state against each project_id;

const phaseCache: Record<string, any> = {};

export async function getPhaseBasedOnProjects({
  pageNumber = 1,
  projectId,
}: any) {
  try {
    if (phaseCache[projectId]) {
      return phaseCache[projectId];
    }

    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: pageNumber,
      endpoint: projectPhaseEndPoint,
      searchParams: {
        project__project_id: projectId,
      },
    });

    const response = await crudService.getAll(paginatedUrl);
    phaseCache[projectId] = response;
    return response;
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

// block cache to persist state against each phase_id;

const blockCache: Record<string, any> = {};

export async function getBlockBasedOnPhase({
  pageNumber = 1,
  phaseId,
  refetch = false,
}: any) {
  try {
    if (blockCache[phaseId] && !refetch) {
      return blockCache[phaseId];
    }

    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: pageNumber,
      endpoint: projectBlockEndPoint,
      searchParams: {
        phase__id: phaseId,
      },
    });

    const response = await crudService.getAll(paginatedUrl);

    blockCache[phaseId] = response;

    return response;
  } catch (error) {
    const message = getErrorMessage(error);
    NotificationManager.error(message);

    return error?.response;
  }
}

export function getPropertyStatusOptions(value: string) {
  if (value === propertyStatusObj?.propertyBooked) {
    return propertyStatusOptions;
  }

  return propertyStatusOptions.filter((item: any) => {
    return item?.label !== propertyStatusObj?.propertyBooked;
  });
}

export function addNoneOptionInProjectDropdown(data: any = {}) {
  if (!data?.projects && !Array.isArray(data?.project)) {
    return {};
  }
  return {
    ...data?.projects,
    objects: [
      ...data?.projects?.objects,
      {
        project_name: noProject,
      },
    ],
  };
}

export function addAllOptionInDropdown(data: any = []) {
  return [...data, { label: "All", key: "all", value: "all" }];
}

export function addNoneOptionInUserDropdown(data: any = []) {
  return [...data, { label: noUser, key: noUser }];
}

export function addNoneOptionInTeamDropdown(data: any = []) {
  return [...data, { label: noTeam, key: noTeam }];
}

export function getLeadStatusOptions(value: string) {
  if (value === leadStatusObj?.statusNew) {
    return leadStatusOptions;
  }

  return leadStatusOptions.filter((item: any) => {
    return item?.label !== leadStatusObj?.statusNew;
  });
}

export function shortenStringByddingDots(
  inputString: string,
  maxLength: number
) {
  if (typeof inputString !== "string") return inputString;

  if (maxLength <= 3) return "...".slice(0, maxLength);

  if (inputString.length > maxLength) {
    return inputString.substring(0, maxLength - 3) + "...";
  }
  return inputString;
}

export function getTaskStatusOptions(value: string) {
  if (value === taskStatusObj?.taskStatusPending) {
    return taskStatusOptions;
  }

  return taskStatusOptions.filter((item: any) => {
    return item?.label !== taskStatusObj?.taskStatusPending;
  });
}

export function stringLowerCaseConvertion(value: any) {
  return value?.toString()?.toLowerCase();
}

export function generateOptionsForPaymentPlan(value: string) {
  if (!value) {
    return [];
  }
  const options = value
    ?.split(/[,|]/)
    .map((item: any) => item?.trim())
    .filter(Boolean);

  return options.map((item: any, idx: number) => {
    return {
      // ...item, it will spread string into individual letters
      label: item,
      value: item + "-" + idx,
    };
  });
}

export function formatNumber(num: number) {
  if (num < 1000) return String(num);

  // numerical seperator not dash, es21

  if (num < 1_000_000)
    return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + "k";

  if (num < 1_000_000_000)
    return (num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1) + "M";

  return (num / 1_000_000_000).toFixed(num % 1_000_000_000 === 0 ? 0 : 1) + "B";
}

// pure function

export function shortenFileName(
  fileName: string,
  maxLength: number = 15
): string {
  if (!fileName) return "";

  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex === -1) return fileName; // no extension

  const base = fileName.substring(0, dotIndex);
  const ext = fileName.substring(dotIndex);

  if (base.length <= maxLength) {
    return fileName;
  }

  return base.substring(0, maxLength) + "..." + ext;
}

export function normalizeKey(key: string) {
  return key
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function normalizeObjectKeys(obj: any) {
  return Object.keys(obj).reduce((acc: any, key: string) => {
    acc[normalizeKey(key)] = obj[key];
    return acc;
  }, {});
}

export function formatPropertyTypeForImport(value: string) {
  if (!value) {
    return "Plot";
  }

  if (value?.toLowerCase() === "flat" || value?.toLowerCase() === "apartment") {
    return "Flat/Apartment";
  }

  return capitalizeFirstLetter(value);
}

export function isPhoneNumberValid(phone: string) {
  const phoneNumber = parsePhoneNumberFromString(phone);
  const isValid = phoneNumber?.isValid() && phoneNumber?.isPossible();

  return isValid;
}

export function handlePhoneFilterChange(
  value: any,
  key: string,
  code: string,
  isValid: boolean,
  setActiveFilters: React.Dispatch<React.SetStateAction<any>>,
  setIsValidPhone: (val: boolean) => void,
  valueType?: string
) {
  if (valueType === "obj") {
    if (value === code) {
      setActiveFilters((prev: any) => ({
        ...prev,
        [key]: "",
        leadId: "",
      }));
      setIsValidPhone(true);
      return;
    }

    setActiveFilters((prev: any) => ({
      ...prev,
      [key]: value?.lead_id ? value?.phone_number : value,
      leadId: value?.lead_id || "",
    }));
    setIsValidPhone(isValid);
    return;
  }

  if (value === code) {
    setActiveFilters((prev: any) => ({
      ...prev,
      [key]: "",
    }));
    setIsValidPhone(true);
    return;
  }

  setActiveFilters((prev: any) => ({
    ...prev,
    [key]: value,
  }));
  setIsValidPhone(isValid);
}

export function formatStartDateForTimestampDate(date: any) {
  return date
    ? moment(date).startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSSSSS")
    : null;
}

export function formatEndDateForTimestampDate(date: any) {
  return date
    ? moment(date).endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSSSSS")
    : null;
}
